const COACH_ENTRY_TYPES = new Set(['ai_message', 'ai_recommendation', 'ai_question']);

export const DEFAULT_EXERCISE_CHAT_EXPANDED = false;

export const exerciseCoachHistoryKey = ({ workoutId = '', exerciseId = '', conversationLength = 0 } = {}) => (
  `${workoutId}:${exerciseId}:${conversationLength}`
);

export const historyScrollTop = ({ scrollHeight = 0, clientHeight = 0 } = {}) => (
  Math.max(0, scrollHeight - clientHeight)
);

export const uniqueExerciseCoachEntries = (entries = []) => {
  const seen = new Set();
  return entries.filter((entry) => {
    const message = entry?.data?.message || entry?.data?.question || entry?.content || '';
    const key = COACH_ENTRY_TYPES.has(entry?.type)
      ? `coach:${entry._conversationExerciseId || ''}:${entry.type}:${message}`
      : entry?._id
        ? `id:${entry._id}`
        : [entry?._conversationExerciseId, entry?.type, entry?.timestamp, message, entry?.data?.set_index, entry?.data?.action_id].join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const exerciseCoachEntryKey = (entry = {}, index = 0) => {
  if (entry._id) return `id:${entry._id}`;
  const message = entry.data?.message || entry.data?.question || entry.content || '';
  return [entry._conversationExerciseId, entry.type, entry.timestamp, message, entry.data?.set_index, entry.data?.action_id, entry._stableKey, index].join('|');
};

export const mergeExerciseCoachConversations = (conversations = []) => (
  conversations.flatMap(({ exerciseId, exerciseName, conversationLog = [] }) => (
    conversationLog.map((entry, index) => ({
      ...entry,
      _conversationExerciseId: exerciseId,
      _conversationExerciseName: exerciseName,
      _conversationIndex: index,
    }))
  )).sort((a, b) => {
    const aTime = new Date(a.timestamp || 0).getTime();
    const bTime = new Date(b.timestamp || 0).getTime();
    const safeATime = Number.isFinite(aTime) ? aTime : Number.MAX_SAFE_INTEGER;
    const safeBTime = Number.isFinite(bTime) ? bTime : Number.MAX_SAFE_INTEGER;
    return (safeATime - safeBTime)
      || String(a._conversationExerciseId).localeCompare(String(b._conversationExerciseId))
      || (a._conversationIndex - b._conversationIndex);
  })
);

export const latestExerciseCoachEntry = (conversation = []) => {
  if (!Array.isArray(conversation)) return '';
  for (let index = conversation.length - 1; index >= 0; index -= 1) {
    const entry = conversation[index];
    if (!COACH_ENTRY_TYPES.has(entry?.type)) continue;
    const text = entry.data?.message || entry.data?.question || entry.content;
    if (typeof text === 'string' && text.trim()) return entry;
  }
  return null;
};

export const latestExerciseCoachMessage = (conversation = []) => {
  const entry = latestExerciseCoachEntry(conversation);
  return entry?.data?.message || entry?.data?.question || entry?.content || '';
};

export const visibleExerciseCoachEntries = ({ conversation = [], expanded = false, fallbackMessage = '' } = {}) => {
  if (expanded) return conversation;
  const latestEntry = latestExerciseCoachEntry(conversation);
  if (latestEntry) return [latestEntry];
  return fallbackMessage ? [{ _stableKey: 'coach-summary', type: 'ai_message', data: { message: fallbackMessage } }] : [];
};
